import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { saveSharingTreeRoster } from "@/lib/sharing-tree-roster";
import { api, formatApiError } from "@/lib/api";
import { trackEvent } from "@/lib/analytics";
import { useAuth } from "@/context/AuthContext";
import { LogOut, Plus, Trash2, Send, TreePine, CheckCircle2, Clock, X, ClipboardCheck, ChevronRight, ChevronLeft, ListOrdered } from "lucide-react";
import { formatPhone, normalizePhoneForSave, isAddressValid } from "@/lib/utils";
import AddressFields from "@/components/AddressFields";
import { useNavigate } from "react-router-dom";
import Logo from "@/components/Logo";
import ToyRequestField, { normalizeToyFields, toyPayload } from "@/components/ToyRequestField";
import useToySuggestions from "@/hooks/useToySuggestions";
import SharingTreeAdditionalInformation from "@/components/SharingTreeAdditionalInformation";
import DistributionCallout from "@/components/DistributionCallout";
import SharingTreeClothingSizes from "@/components/SharingTreeClothingSizes";
import { DEFAULT_DIETARY_OPTIONS, loadDietaryOptions } from "@/lib/dietary-options";
import { portalMembersPayload, unusedPortalMember } from "@/lib/portal-members";

const RELATIONSHIPS = ["Child", "FosterChild", "Friend", "GrandChild", "GrandParent", "GreatGrandChild", "GreatGrandParent", "Parent", "Sibling", "Spouse"];
const GENDERS = ["Female", "Male", "Non-binary", "Prefer not to say"];
export default function ClientPortal() {
  const [me, setMe] = useState(null);
  const [form, setForm] = useState({});
  const [members, setMembers] = useState([]);
  const [initial, setInitial] = useState({ form: {}, members: [] });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);
  const [dietaryOptions, setDietaryOptions] = useState(DEFAULT_DIETARY_OPTIONS);
  const memberGenderFields = useRef([]);
  const [showMemberGenderErrors, setShowMemberGenderErrors] = useState(false);
  const { logout } = useAuth();
  const navigate = useNavigate();

  async function load() {
    try {
      const [{ data }, options] = await Promise.all([
        api.get("/portal/me"),
        loadDietaryOptions(),
      ]);
      setMe(data);
      setDietaryOptions(options);
      const initForm = {
        first_name: data.first_name, last_name: data.last_name,
        phone: data.phone || "", email: data.email || "", address: data.address || "",
        gender: data.gender || "", birth_year: data.birth_year || null,
        dietary_restrictions: data.dietary_restrictions || [],
      };
      const initMembers = data.household_members.map((m) => ({
        id: m.id, first_name: m.first_name || "", last_name: m.last_name || "",
        gender: m.gender || "", birth_year: m.birth_year || null,
        birth_month: m.birth_month || null, relationship: m.relationship || "",
        phone: m.phone || "", email: m.email || "",
      }));
      setForm(initForm);
      setMembers(initMembers);
      setInitial({ form: initForm, members: initMembers });
      setSuccess("");
    } catch (e) { setError(formatApiError(e)); }
  }
  useEffect(() => { load(); }, []);

  // Detect whether anything has actually changed vs the loaded snapshot
  const hasChanges = useMemo(() => {
    const formChanged = JSON.stringify(form) !== JSON.stringify(initial.form);
    const normalizeMembers = (arr) => portalMembersPayload(arr).map((m) => ({
      first_name: m.first_name || "", last_name: m.last_name || "",
      gender: m.gender || "", birth_year: m.birth_year || null,
      birth_month: m.birth_month || null, relationship: m.relationship || "",
      phone: m.phone || "", email: m.email || "",
    }));
    const membersChanged = JSON.stringify(normalizeMembers(members)) !== JSON.stringify(normalizeMembers(initial.members));
    return formChanged || membersChanged;
  }, [form, members, initial]);

  async function submit() {
    setError(""); setSuccess(""); setSaving(true);
    if (!isAddressValid(form.address)) {
      setError("Address is incomplete. Please fill in Street, City, State, and a 5-digit Zip.");
      setSaving(false);
      return;
    }
    try {
      const cleanMembers = portalMembersPayload(members);
      const incomplete = cleanMembers.findIndex(m => !(m.first_name || "").trim());
      if (incomplete >= 0) {
        setError(`Household member ${incomplete + 1}: enter a first name or remove the incomplete row.`);
        return;
      }
      const missingGender = members.findIndex(m => !unusedPortalMember(m) && !GENDERS.includes(m.gender));
      setShowMemberGenderErrors(true);
      if (missingGender >= 0) {
        setError(`Household member ${missingGender + 1}: select a gender or Prefer not to say.`);
        memberGenderFields.current[missingGender]?.focus();
        return;
      }
      const proposed = { ...form };
      if (proposed.birth_year) proposed.birth_month = 1;
      await api.post("/portal/updates", {
        proposed_changes: proposed,
        proposed_members: cleanMembers,
      });
      trackEvent("portal_profile_update_submitted");
      setSuccess("Your changes have been submitted for staff review. Thank you!");
      setInitial({ form: { ...form }, members: members.map((m) => ({ ...m })) });
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  function addMember() {
    setMembers([...members, { first_name: "", last_name: "", gender: "", birth_year: null, birth_month: null, relationship: "Child", _new: true }]);
  }
  function removeMember(i) { setMembers(members.filter((_, j) => j !== i)); }

  if (!me) return <div className="p-8 text-sm" style={{ color: "var(--fb-text-muted)" }}>Loading…</div>;

  return (
    <div className="min-h-screen" style={{ background: "var(--fb-bg)" }} data-testid="client-portal">
      {/* Top nav */}
      <header className="bg-white border-b" style={{ borderColor: "var(--fb-border)" }}>
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={36} />
            <div>
              <div className="font-bold text-sm" style={{ fontFamily: "Outfit" }}>Pullman Child Welfare</div>
              <div className="text-xs" style={{ color: "var(--fb-text-muted)" }}>Client Portal</div>
            </div>
          </div>
          <button data-testid="portal-logout" onClick={async () => { await logout(); navigate("/portal/login"); }}
                  className="inline-flex items-center gap-1.5 text-sm font-medium hover:underline">
            <LogOut className="w-4 h-4" /> Sign out
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-8 sm:py-10 space-y-6 page-enter">
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold" style={{ fontFamily: "Outfit" }}>Welcome, {me.first_name}.</h1>
          <p className="text-base sm:text-lg mt-2" style={{ color: "var(--fb-text-muted)" }}>
            Review and update your information. Changes go to staff for approval.
          </p>
        </div>

        <CheckInCard me={me} />

        {success && (
          <div className="fb-status-success p-4 rounded-xl text-sm font-semibold" data-testid="success-msg">{success}</div>
        )}
        {error && (
          <div id="portal-profile-error" role="alert" className="fb-status-error p-4 rounded-xl text-sm" data-testid="portal-error">{error}</div>
        )}

        <div className="fb-card p-4 sm:p-6 space-y-4">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Your Information</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <PortalField label="First Name" value={form.first_name} onChange={(v) => setForm({ ...form, first_name: v })} testid="portal-firstname" />
            <PortalField label="Last Name" value={form.last_name} onChange={(v) => setForm({ ...form, last_name: v })} testid="portal-lastname" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Gender</label>
              <select
                data-testid="portal-gender"
                value={form.gender || ""}
                onChange={(e) => setForm({ ...form, gender: e.target.value })}
                className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] bg-white text-base"
                style={{ borderColor: "var(--fb-border)" }}
              >
                <option value="">— Select —</option>
                {form.gender && !GENDERS.includes(form.gender) && <option value={form.gender}>{form.gender} (current)</option>}
                {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1.5">Age</label>
              <input
                data-testid="portal-age"
                type="number" min="0" max="120"
                value={form.birth_year ? new Date().getFullYear() - form.birth_year : ""}
                onChange={(e) => {
                  const a = parseInt(e.target.value);
                  setForm({
                    ...form,
                    birth_year: isNaN(a) ? null : new Date().getFullYear() - a,
                  });
                }}
                placeholder="Age in years"
                className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] text-base tabular-nums"
                style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              />
            </div>
          </div>
          <PortalField label="Phone" value={formatPhone(form.phone)} onChange={(v) => setForm({ ...form, phone: v })} onBlur={() => setForm((prev) => ({ ...prev, phone: normalizePhoneForSave(prev.phone) }))} testid="portal-phone" />
          <PortalField label="Email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="portal-email" />
          <AddressFields allowGoogleCheck={false} value={form.address} onChange={(v) => setForm((f) => ({ ...f, address: v, google_verification_token: "" }))} onVerification={(t) => setForm((f) =>
            (f.google_verification_token || "") === (t || "") ? f : { ...f, google_verification_token: t || "" }
          )} />

          <div>
            <label className="block text-sm font-medium mb-2">Dietary Restrictions</label>
            <div className="flex flex-wrap gap-2" data-testid="portal-dietary-restrictions">
              {[...new Set([...dietaryOptions, ...(form.dietary_restrictions || [])])].map((opt) => {
                const selected = (form.dietary_restrictions || []).includes(opt);
                return (
                  <button
                    key={opt}
                    type="button"
                    onClick={() => {
                      const current = form.dietary_restrictions || [];
                      const next = selected ? current.filter((x) => x !== opt) : [...current, opt];
                      setForm({ ...form, dietary_restrictions: next });
                    }}
                    className="px-3 py-2 rounded-full text-sm font-medium border-2 transition-colors"
                    style={{
                      borderColor: selected ? "var(--fb-primary)" : "var(--fb-border)",
                      background: selected ? "var(--fb-primary)" : "white",
                      color: selected ? "white" : "var(--fb-text)",
                    }}
                    data-testid={`portal-diet-${opt}`}
                  >
                    {opt}
                  </button>
                );
              })}
            </div>
            <p className="text-xs mt-2" style={{ color: "var(--fb-text-muted)" }}>
              Select all that apply. These help staff choose appropriate food items for your household.
            </p>
          </div>
        </div>

        <div className="fb-card p-4 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Household Members</h3>
            <button data-testid="portal-add-member" onClick={addMember}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold px-4 py-2 rounded-lg border hover:bg-stone-50"
                    style={{ borderColor: "var(--fb-border)" }}>
              <Plus className="w-4 h-4" /> Add member
            </button>
          </div>
          <div className="space-y-2">
            {members.length > 0 && (
              <div className="hidden md:grid grid-cols-12 gap-2 px-3 pb-2 text-xs uppercase tracking-wider font-semibold" style={{ color: "#8B918B" }}>
                <div className="col-span-2">First Name</div>
                <div className="col-span-2">Last Name</div>
                <div className="col-span-2 text-center">Age</div>
                <div className="col-span-2">Gender</div>
                <div className="col-span-3">Relationship</div>
                <div className="col-span-1 text-right">Remove</div>
              </div>
            )}
            {members.map((m, i) => {
              const currentYear = new Date().getFullYear();
              const age = m.birth_year ? currentYear - m.birth_year : "";
              return (
                <div key={i} className="grid grid-cols-6 md:grid-cols-12 gap-2 items-center p-3 rounded-xl border" style={{ borderColor: "var(--fb-border)" }}>
                  <input value={m.first_name || ""} onChange={(e) => setMembers(members.map((x, j) => j === i ? { ...x, first_name: e.target.value } : x))}
                         placeholder="First name" aria-label="First name"
                         className="col-span-3 md:col-span-2 min-w-0 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
                  <input value={m.last_name || ""} onChange={(e) => setMembers(members.map((x, j) => j === i ? { ...x, last_name: e.target.value } : x))}
                         placeholder="Last name" aria-label="Last name"
                         className="col-span-3 md:col-span-2 min-w-0 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
                  <input type="number" min="0" max="120" value={age} aria-label="Age in years"
                         onChange={(e) => {
                           const a = parseInt(e.target.value);
                           setMembers(members.map((x, j) => j === i ? {
                             ...x,
                             birth_year: isNaN(a) ? null : currentYear - a,
                             birth_month: x.birth_month || 1,
                           } : x));
                         }}
                         placeholder="Age"
                         className="col-span-2 md:col-span-2 px-3 py-2 rounded-lg border tabular-nums text-center" style={{ borderColor: "var(--fb-border)" }} />
                  <div className="col-span-4 md:col-span-2 min-w-0">
                    <label htmlFor={`portal-member-gender-${i}`} className="block text-xs font-medium mb-1 md:sr-only">Gender (member {i + 1})</label>
                    <select id={`portal-member-gender-${i}`} data-testid={`portal-member-gender-${i}`}
                            ref={el => { memberGenderFields.current[i] = el; }}
                            value={m.gender || ""} required={!unusedPortalMember(m)}
                            aria-invalid={showMemberGenderErrors && !unusedPortalMember(m) && !GENDERS.includes(m.gender)}
                            aria-describedby={error && showMemberGenderErrors && !unusedPortalMember(m) && !GENDERS.includes(m.gender) ? "portal-profile-error" : undefined}
                            title={m.gender || "Select gender"}
                            onChange={e => setMembers(members.map((x, j) => j === i ? { ...x, gender: e.target.value } : x))}
                            className="w-full min-w-0 px-2 py-2 rounded-lg border bg-white"
                            style={{ borderColor: showMemberGenderErrors && !unusedPortalMember(m) && !GENDERS.includes(m.gender) ? "var(--fb-error)" : "var(--fb-border)" }}>
                      <option value="">Select gender</option>
                      {m.gender && !GENDERS.includes(m.gender) && <option value={m.gender}>{m.gender} (current)</option>}
                      {GENDERS.map(g => <option key={g} value={g}>{g}</option>)}
                    </select>
                  </div>
                  <select value={m.relationship || ""} onChange={(e) => setMembers(members.map((x, j) => j === i ? { ...x, relationship: e.target.value } : x))}
                          aria-label="Relationship"
                          className="col-span-5 md:col-span-3 min-w-0 px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                    <option value="">Relationship</option>
                    {m.relationship && !RELATIONSHIPS.includes(m.relationship) && <option value={m.relationship}>{m.relationship} (current)</option>}
                    {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <button onClick={() => removeMember(i)} aria-label="Remove member"
                          className="col-span-1 md:col-span-1 p-2 rounded-lg hover:bg-rose-50 flex justify-center">
                    <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                  </button>
                </div>
              );
            })}
          </div>
        </div>

        <button data-testid="portal-submit-btn" onClick={submit} disabled={saving || !hasChanges}
                className="w-full fb-btn-primary py-4 rounded-xl text-lg font-semibold inline-flex items-center justify-center gap-2">
          <Send className="w-5 h-5" />
          {saving ? "Submitting…" : hasChanges ? "Submit changes for approval" : "No changes to submit"}
        </button>

        <SharingTreeCard me={me} />
      </main>
    </div>
  );
}

function formatTime12h(t) {
  if (!t) return "";
  const [h, m] = t.split(":").map(Number);
  if (isNaN(h)) return t;
  const ampm = h >= 12 ? "PM" : "AM";
  const hour = h % 12 || 12;
  return m === 0 ? `${hour}:00 ${ampm}` : `${hour}:${String(m).padStart(2, "0")} ${ampm}`;
}

function CheckInCard({ me }) {
  const [settings, setSettings] = useState(null);
  const [checkinStatus, setCheckinStatus] = useState(null);
  const [statusLoaded, setStatusLoaded] = useState(false);
  const [showModal, setShowModal] = useState(false);

  const load = useCallback(async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        api.get("/checkin-settings"),
        api.get("/portal/checkin/status"),
      ]);
      setSettings(sRes.data);
      setCheckinStatus(cRes.data);
    } catch (e) {
      // If checkin/status returns 403 (not a client) or other error, just don't show
    } finally {
      setStatusLoaded(true);
    }
  }, []);

  // Initial load on mount
  useEffect(() => {
    load();
  }, [load]);

  // Interval management: slow down once the window is open, stop once terminal
  useEffect(() => {
    const windowOpen = settings?.is_currently_open;
    const alreadyCheckedIn = checkinStatus?.checked_in;
    const served = checkinStatus?.status === "done";

    // Stop polling once the client is fully served
    if (served) return;

    // Stop polling if the window has closed and the client is already checked in —
    // nothing useful will change for this session
    if (!windowOpen && alreadyCheckedIn) return;

    // Poll every 60s while the window is open (queue position may update),
    // every 30s while the window is closed (waiting for it to open)
    const delay = windowOpen ? 60000 : 30000;
    const interval = setInterval(load, delay);
    return () => clearInterval(interval);
  }, [load, settings, checkinStatus]);

  if (!settings) return null;

  if (!settings.is_currently_open) {
    const openFmt = formatTime12h(settings.open_time);
    const closeFmt = formatTime12h(settings.close_time);
    const windowText = openFmt && closeFmt
      ? `Available Saturdays ${openFmt} – ${closeFmt}`
      : null;
    return (
      <div className="fb-card p-5 sm:p-6" data-testid="portal-checkin-card-closed"
           style={{ borderLeft: "4px solid var(--fb-border)", opacity: 0.75 }}>
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
               style={{ background: "var(--fb-bg)", border: "1.5px solid var(--fb-border)" }}>
            <ClipboardCheck className="w-5 h-5" style={{ color: "var(--fb-text-muted)" }} />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Saturday Check-In</h3>
            <p className="text-sm mt-0.5" style={{ color: "var(--fb-text-muted)" }}>
              Check-in is not available right now.
            </p>
            {windowText && (
              <p className="text-sm font-medium mt-1" style={{ color: "var(--fb-text-muted)" }}>
                <Clock className="w-3.5 h-3.5 inline mr-1 -mt-0.5" />
                {windowText}
              </p>
            )}
          </div>
        </div>
      </div>
    );
  }

  const alreadyCheckedIn = checkinStatus?.checked_in;
  const checkinDone = alreadyCheckedIn && checkinStatus?.status === "done";

  return (
    <div className="fb-card p-5 sm:p-6 relative overflow-hidden" data-testid="portal-checkin-card"
         style={{ borderLeft: `4px solid ${checkinDone ? "var(--fb-success)" : "var(--fb-primary)"}` }}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
             style={{ background: checkinDone ? "var(--fb-success)" : "var(--fb-primary)", color: "white" }}>
          <ClipboardCheck className="w-5 h-5" />
        </div>
        <div className="flex-1">
          <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Saturday Check-In</h3>
          <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
            {checkinDone ? "Your visit for today is complete." : "Check in now so staff can prepare for your visit."}
          </p>
        </div>
      </div>

      {alreadyCheckedIn && checkinStatus?.status === "serving" ? (
        <div className="flex items-start gap-3 p-4 rounded-xl" data-testid="being-served-banner"
             style={{ background: "var(--fb-primary)", color: "white" }}>
          <ChevronRight className="w-6 h-6 mt-0.5 shrink-0 animate-pulse" />
          <div>
            <div className="font-bold text-base">You've been called — please come to the counter!</div>
            <div className="text-sm mt-0.5 opacity-80">Staff are ready for you now.</div>
          </div>
        </div>
      ) : checkinDone ? (
        <div className="flex items-start gap-3 p-3 rounded-xl fb-status-success" data-testid="already-served-banner">
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-sm">You've been served today — see you next Saturday!</div>
            {checkinStatus.item_selections?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {checkinStatus.item_selections.map((item) => (
                  <span key={item} className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: "var(--fb-success)20", color: "var(--fb-success)" }}>
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : alreadyCheckedIn ? (
        <div className="flex items-start gap-3 p-3 rounded-xl fb-status-success" data-testid="already-checked-in">
          <CheckCircle2 className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <div className="font-semibold text-sm">You're checked in!</div>
            <div className="text-sm mt-0.5">
              Queue position: <span className="font-bold tabular-nums">#{checkinStatus.position}</span>
            </div>
            {checkinStatus.item_selections?.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {checkinStatus.item_selections.map((item) => (
                  <span key={item} className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
                    {item}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : statusLoaded ? (
        <button
          onClick={() => {
            trackEvent("portal_checkin_started");
            setShowModal(true);
          }}
          className="fb-btn-primary px-5 py-3 rounded-xl font-semibold inline-flex items-center gap-2 text-sm"
          data-testid="start-checkin-btn"
        >
          <ClipboardCheck className="w-4 h-4" /> Check In Now
        </button>
      ) : (
        <div className="h-10 w-32 rounded-xl animate-pulse" style={{ background: "var(--fb-border)" }} data-testid="checkin-status-loading" />
      )}

      {showModal && (
        <CheckInModal
          me={me}
          settings={settings}
          onClose={() => setShowModal(false)}
          onDone={() => { setShowModal(false); load(); }}
        />
      )}
    </div>
  );
}

function CheckInModal({ me, settings, onClose, onDone }) {
  const [step, setStep] = useState(1);
  const [selectedItems, setSelectedItems] = useState([]);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);

  const availableGroups = (settings.groups || []).filter(
    (g) => (g.items || []).some((i) => i.available !== false)
  );

  function toggleItem(label, group) {
    setSelectedItems((prev) => {
      if (prev.includes(label)) return prev.filter((i) => i !== label);
      const item = (group.items || []).find((i) => i.label === label);
      if (item?.exempt) return [...prev, label];
      const nonExemptLabels = (group.items || []).filter((i) => !i.exempt).map((i) => i.label);
      const selectedInGroup = prev.filter((i) => nonExemptLabels.includes(i)).length;
      if (selectedInGroup >= group.max_select) return prev;
      return [...prev, label];
    });
  }

  function isGroupFull(group) {
    const nonExemptLabels = (group.items || []).filter((i) => !i.exempt).map((i) => i.label);
    return selectedItems.filter((s) => nonExemptLabels.includes(s)).length >= group.max_select;
  }

  async function submit() {
    setSaving(true); setError("");
    try {
      const { data } = await api.post("/portal/checkin", {
        item_selections: selectedItems,
        notes,
      });
      trackEvent("portal_checkin_completed");
      setResult(data);
      setStep(4);
    } catch (e) {
      if (e.response?.status === 409) {
        // Already checked in — show friendly message and close modal so status reloads
        const msg = e.response?.data?.detail || "You're already checked in for today.";
        setError(msg);
        // Delay close so the user can read the message, then refresh status
        setTimeout(() => { onDone(); }, 2500);
      } else {
        setError(formatApiError(e));
      }
    }
    finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-lg my-8 p-6 sm:p-7" onClick={(e) => e.stopPropagation()} data-testid="checkin-modal">

        {/* Header */}
        <div className="flex items-start justify-between mb-1">
          <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Check In</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>

        {/* Step indicator */}
        {step < 4 && (
          <div className="flex items-center gap-1 mb-5">
            {[1, 2, 3].map((s) => (
              <div key={s} className="h-1 flex-1 rounded-full transition-colors"
                style={{ background: s <= step ? "var(--fb-primary)" : "var(--fb-border)" }} />
            ))}
          </div>
        )}

        {/* Step 1: Verify info */}
        {step === 1 && (
          <div>
            <p className="text-sm mb-4" style={{ color: "var(--fb-text-muted)" }}>
              Please confirm your information is current. If anything needs updating, close this and use the form above.
            </p>
            <div className="space-y-2 mb-5">
              {[
                ["Name", `${me.first_name} ${me.last_name}`.trim()],
                ["Phone", me.phone ? formatPhone(me.phone) : "—"],
                ["Email", me.email || "—"],
                ["Address", me.address || "—"],
              ].map(([label, value]) => (
                <div key={label} className="flex items-center gap-3 p-3 rounded-xl" style={{ background: "var(--fb-bg)", border: "1px solid var(--fb-border)" }}>
                  <div className="text-xs font-semibold uppercase tracking-wider w-16 shrink-0" style={{ color: "#8B918B" }}>{label}</div>
                  <div className="text-sm">{value}</div>
                </div>
              ))}
            </div>
            <div className="flex justify-end gap-3">
              <button onClick={onClose} className="px-4 py-2.5 rounded-lg border text-sm" style={{ borderColor: "var(--fb-border)" }}>
                Needs updating
              </button>
              <button onClick={() => setStep(availableGroups.length > 0 ? 2 : 3)}
                className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5">
                Looks good <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Item requests */}
        {step === 2 && (
          <div>
            <p className="text-sm mb-4" style={{ color: "var(--fb-text-muted)" }}>
              Select any items you'd like. Greyed-out options are unavailable today.
            </p>
            <div className="space-y-4 mb-5">
              {availableGroups.map((group) => {
                const nonExemptLabels = (group.items || []).filter((i) => !i.exempt).map((i) => i.label);
                const selectedInGroup = selectedItems.filter((s) => nonExemptLabels.includes(s)).length;
                const full = selectedInGroup >= group.max_select;
                return (
                  <div key={group.id}>
                    <div className="text-xs font-semibold uppercase tracking-wider mb-2 flex items-center justify-between" style={{ color: "#8B918B" }}>
                      <span>{group.name}</span>
                      <span>{selectedInGroup}/{group.max_select}</span>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      {(group.items || []).map((item) => {
                        const isUnavailable = item.available === false;
                        const isSelected = selectedItems.includes(item.label);
                        const isDisabled = isUnavailable || (!item.exempt && full && !isSelected);
                        return (
                          <div key={item.id} className="flex flex-col items-center gap-0.5">
                            <button
                              onClick={() => !isDisabled && toggleItem(item.label, group)}
                              disabled={isDisabled}
                              data-testid={`item-btn-${item.label}`}
                              className="px-4 py-2 rounded-full text-sm font-medium border-2 transition-colors"
                              style={{
                                borderColor: isSelected ? "var(--fb-primary)" : isUnavailable ? "var(--fb-border)" : item.exempt ? "#16a34a" : "var(--fb-border)",
                                background: isSelected ? "var(--fb-primary)" : isDisabled ? "#F0F0EE" : item.exempt ? "#f0fdf4" : "white",
                                color: isSelected ? "white" : isDisabled ? "#B0B5B0" : "var(--fb-text)",
                                cursor: isDisabled ? "not-allowed" : "pointer",
                              }}
                            >
                              {item.label}{isUnavailable ? " (unavailable)" : ""}
                            </button>
                            {item.exempt && !isUnavailable && (
                              <span className="text-xs font-medium" style={{ color: "#16a34a" }}>
                                Always available
                              </span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="flex justify-between gap-3">
              <button onClick={() => setStep(1)} className="inline-flex items-center gap-1 text-sm px-4 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={() => setStep(3)} className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5">
                Next <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Notes */}
        {step === 3 && (
          <div>
            {settings.custom_message && (
              <div className="mb-4 p-3 rounded-xl fb-status-warning text-sm">
                <span className="font-semibold">Note from staff: </span>{settings.custom_message}
              </div>
            )}
            <p className="text-sm mb-3" style={{ color: "var(--fb-text-muted)" }}>
              Any special requests or information for staff? (Optional)
            </p>
            <textarea
              rows={3}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="e.g. I have a mobility issue, please have someone bring my bags out."
              className="w-full px-4 py-3 rounded-xl border resize-none text-sm mb-4"
              style={{ borderColor: "var(--fb-border)" }}
              data-testid="checkin-notes"
            />
            {error && <div className="fb-status-error text-sm p-3 rounded-lg mb-3">{error}</div>}
            <div className="flex justify-between gap-3">
              <button onClick={() => setStep(availableGroups.length > 0 ? 2 : 1)}
                className="inline-flex items-center gap-1 text-sm px-4 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>
                <ChevronLeft className="w-4 h-4" /> Back
              </button>
              <button onClick={submit} disabled={saving}
                className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold text-sm inline-flex items-center gap-1.5"
                data-testid="checkin-submit-btn">
                {saving ? "Checking in…" : <><CheckCircle2 className="w-4 h-4" /> Check In</>}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Confirmation */}
        {step === 4 && result && (
          <div className="text-center py-4">
            <div className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ background: "var(--fb-success)20", color: "var(--fb-success)" }}>
              <CheckCircle2 className="w-8 h-8" />
            </div>
            <h4 className="text-2xl font-bold mb-2" style={{ fontFamily: "Outfit" }}>You're checked in!</h4>
            <p className="text-sm mb-4" style={{ color: "var(--fb-text-muted)" }}>
              Staff will call you when it's your turn.
            </p>
            <div className="inline-flex items-center gap-2 px-6 py-4 rounded-2xl mb-5"
              style={{ background: "var(--fb-primary)10", color: "var(--fb-primary)" }}>
              <ListOrdered className="w-5 h-5" />
              <span className="text-xl font-bold tabular-nums" style={{ fontFamily: "Outfit" }}>
                Queue position #{result.position}
              </span>
            </div>
            {selectedItems.length > 0 && (
              <div className="flex flex-wrap justify-center gap-1.5 mb-5">
                {selectedItems.map((item) => (
                  <span key={item} className="text-xs px-2 py-0.5 rounded-full font-medium"
                    style={{ background: "var(--fb-primary)15", color: "var(--fb-primary)" }}>
                    {item}
                  </span>
                ))}
              </div>
            )}
            <button onClick={onDone} className="fb-btn-primary px-6 py-3 rounded-xl font-semibold">
              Done
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function SharingTreeCard({ me }) {
  const [status, setStatus] = useState(null);
  const [showModal, setShowModal] = useState(false);

  async function load() {
    const { data } = await api.get("/portal/sharing-tree/status");
    setStatus(data);
  }
  useEffect(() => { load(); }, []);

  if (!status) return null;

  function formatDate(d) {
    if (!d) return "";
    try { return new Date(d + "T00:00:00").toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }); }
    catch { return d; }
  }

  return (
    <div className="fb-card p-5 sm:p-6 relative overflow-hidden" data-testid="portal-sharing-tree-card">
      <div className="absolute -right-6 -top-6 w-24 h-24 rounded-full opacity-10" style={{ background: "var(--fb-primary)" }} />
      <div className="relative">
        <div className="flex items-start gap-3 mb-3">
          <div className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--fb-primary)", color: "white" }}>
            <TreePine className="w-5 h-5" />
          </div>
          <div className="flex-1">
            <h3 className="text-xl font-semibold" style={{ fontFamily: "Outfit" }}>Sharing Tree {status.year}</h3>
            <p className="text-sm" style={{ color: "var(--fb-text-muted)" }}>
              Holiday gift program: toys for children 9 &amp; under, gift cards for ages 10–18, and food vouchers.
            </p>
          </div>
        </div>

        {/* Status banner */}
        {!status.configured && (
          <div className="text-sm p-3 rounded-lg" style={{ background: "var(--fb-bg)", color: "var(--fb-text-muted)" }}>
            Signups for {status.year} have not been scheduled yet. Check back later.
          </div>
        )}
        {status.configured && (
          <div className="mb-3">
            <DistributionCallout period={status} year={status.year} />
          </div>
        )}
        {status.configured && !status.is_open && (
          <div className="text-sm p-3 rounded-lg flex items-start gap-2 fb-status-warning" data-testid="signup-closed-msg">
            <Clock className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">Signup is closed.</div>
              <div>This year's window: {formatDate(status.open_date)} — {formatDate(status.close_date)}.</div>
              {status.already_signed_up && status.pickup_window && (
                <div className="mt-1" data-testid="pickup-window-display">
                  <span className="font-semibold">Your pickup window:</span> {status.pickup_window}
                </div>
              )}
            </div>
          </div>
        )}
        {status.configured && status.is_open && status.already_signed_up && (
          <div className="text-sm p-3 rounded-lg flex items-start gap-2 fb-status-success" data-testid="signup-done-msg">
            <CheckCircle2 className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <div className="font-semibold">You're signed up for {status.year}.</div>
              {status.pickup_window && (
                <div className="mt-1" data-testid="pickup-window-display">
                  <span className="font-semibold">Your pickup window:</span> {status.pickup_window}
                </div>
              )}
              <div className={status.pickup_window ? "mt-1" : ""}>You can update your submission until {formatDate(status.close_date)}.</div>
              <button onClick={() => {
                trackEvent("portal_sharing_tree_started", { mode: "update" });
                setShowModal(true);
              }} className="text-xs font-semibold mt-1 underline" data-testid="update-signup-btn">
                Update my submission
              </button>
            </div>
          </div>
        )}
        {status.configured && status.is_open && !status.already_signed_up && (
          <div className="mt-2">
            <div className="text-sm mb-3" style={{ color: "var(--fb-text-muted)" }}>
              Open through <span className="font-semibold" style={{ color: "var(--fb-text)" }}>{formatDate(status.close_date)}</span>.
            </div>
            <button onClick={() => {
              trackEvent("portal_sharing_tree_started", { mode: "new" });
              setShowModal(true);
            }} data-testid="portal-signup-btn"
                    className="fb-btn-primary px-5 py-3 rounded-xl font-semibold inline-flex items-center gap-2 text-sm">
              <TreePine className="w-4 h-4" /> Sign Up Now
            </button>
          </div>
        )}
      </div>

      {showModal && <PortalSignupModal year={status.year} me={me} period={status} onClose={() => setShowModal(false)} onDone={() => { setShowModal(false); load(); }} />}
    </div>
  );
}

export function PortalSignupModal({ year, me, period, onClose, onDone }) {
  const genderSelects = useRef([]);
  const [showGenderErrors, setShowGenderErrors] = useState(false);
  const currentYear = new Date().getFullYear();
  const [members, setMembers] = useState(() => {
    if (!me) return [];
    const primaryAge = me.birth_year ? currentYear - me.birth_year : "";
    return [
      { name: `${me.first_name} ${me.last_name}`.trim(), age: primaryAge, gender: me.gender || "", toy_items: [], toy_notes: "", relationship: "Self" },
      ...(me.household_members || []).map((m) => ({
        name: `${m.first_name || ""} ${m.last_name || ""}`.trim(),
        age: m.birth_year ? currentYear - m.birth_year : "",
        gender: m.gender || "",
        toy_items: [], toy_notes: "",
        relationship: m.relationship || "",
      })),
    ];
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [familyItem, setFamilyItem] = useState("");
  const [rosterToken, setRosterToken] = useState(null);
  const [additionalInformation, setAdditionalInformation] = useState("");
  const [membersLoading, setMembersLoading] = useState(true);
  const [membersLoadError, setMembersLoadError] = useState(false);
  const toySuggestions = useToySuggestions("client");

  useEffect(() => {
    let live = true;
    setMembersLoading(true);
    setMembersLoadError(false);
    api.get(`/portal/sharing-tree/signup-members?year=${year}`)
      .then(({ data }) => {
        if (!live) return;
        setRosterToken(data.roster_token || null);
        setAdditionalInformation(data.additional_information || "");
        if (data.members !== null) {
          setMembers(data.members.map(m => ({
            ...m, age: String(m.age), gender: m.gender || "", ...normalizeToyFields(m),
          })));
          setFamilyItem(data.family_item || "");
        }
      })
      .catch(e => {
        if (live) { setMembersLoadError(true); setError(formatApiError(e)); }
      })
      .finally(() => { if (live) setMembersLoading(false); });
    return () => { live = false; };
  }, [year]);

  function update(i, patch) {
    setMembers(members.map((x, j) => j === i ? { ...x, ...patch } : x));
  }
  function add() { setMembers([...members, { name: "", age: "", gender: "", toy_items: [], toy_notes: "", relationship: "" }]); }
  function remove(i) { setMembers(members.filter((_, j) => j !== i)); }

  async function submit() {
    if (membersLoading || membersLoadError) return;
    const missingGender = members.findIndex(m => !GENDERS.includes(m.gender));
    setShowGenderErrors(missingGender >= 0);
    if (missingGender >= 0) {
      setError("Please select a gender for every household member.");
      genderSelects.current[missingGender]?.focus();
      return;
    }
    setError(""); setSaving(true);
    try {
      const clean = members.filter((m) => m.age !== "" && m.age !== null).map((m) => ({
        ...(m.id ? { id: m.id } : {}),
        name: m.name || "",
        age: parseInt(m.age) || 0,
        gender: m.gender || "",
        ...toyPayload({ ...m, legacy_gift_request: "" }),
        ...(parseInt(m.age) <= 9 ? {
          pant_size: m.pant_size || "",
          shirt_size: m.shirt_size || "",
          pj_size: m.pj_size || "",
        } : {}),
        relationship: m.relationship || "",
      }));
      await saveSharingTreeRoster("post", "/portal/sharing-tree/signup", { year, members: clean, family_item: familyItem || "",
        expected_roster_token: rosterToken,
        additional_information: additionalInformation });
      trackEvent("portal_sharing_tree_submitted", { mode: period.already_signed_up ? "update" : "new" });
      onDone();
    } catch (e) { setError(formatApiError(e)); }
    finally { setSaving(false); }
  }

  if (membersLoading || membersLoadError) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={onClose}>
        <div className="bg-white rounded-2xl p-6 max-w-lg" onClick={e => e.stopPropagation()} data-testid="portal-signup-modal">
          <p>{membersLoading ? "Loading your saved signup…" : error}</p>
          <button onClick={onClose} className="mt-4 px-4 py-2 border rounded-lg">Close</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center p-4 bg-black/40 overflow-y-auto" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl my-8 p-6 sm:p-7 max-h-[90vh] overflow-auto" onClick={(e) => e.stopPropagation()} data-testid="portal-signup-modal">
        <div className="flex items-start justify-between mb-2">
          <h3 className="text-2xl font-bold" style={{ fontFamily: "Outfit" }}>Sharing Tree {year} — Sign Up</h3>
          <button onClick={onClose} aria-label="Close"><X className="w-5 h-5" /></button>
        </div>
        <p className="text-sm mb-5" style={{ color: "var(--fb-text-muted)" }}>
          Confirm your household roster. Toy requests apply only to ages 9 and under. Ages 10–18 receive gift cards (no specific request needed). Ages 19+ count toward your food voucher amount.
        </p>

        {period && (
          <div className="mb-5">
            <DistributionCallout period={period} year={year} />
          </div>
        )}

        <div className="space-y-2 mb-4">
          {members.map((m, i) => {
            const ageNum = parseInt(m.age);
            const isToy = !isNaN(ageNum) && ageNum <= 9;
            const tag = isToy ? "Toy" : (ageNum >= 10 && ageNum <= 18) ? "Gift card" : ageNum >= 19 ? "Adult" : "—";
            const tagBg = isToy ? "var(--fb-accent)" : (ageNum >= 10 && ageNum <= 18) ? "var(--fb-success)" : "#8B918B";
            return (
              <div key={i} className="p-3 rounded-xl border space-y-2" style={{ borderColor: "var(--fb-border)" }} data-testid={`portal-signup-member-${i}`}>
                <div className="grid grid-cols-6 md:grid-cols-12 gap-2">
                  <input placeholder="Name" value={m.name} onChange={(e) => update(i, { name: e.target.value })}
                         className="col-span-4 md:col-span-3 min-w-0 px-3 py-2 rounded-lg border" style={{ borderColor: "var(--fb-border)" }} />
                  <input type="number" min="0" max="120" placeholder="Age" value={m.age} onChange={(e) => update(i, { age: e.target.value })}
                         aria-label="Age in years"
                         className="col-span-2 md:col-span-2 min-w-0 px-2 py-2 rounded-lg border tabular-nums text-center" style={{ borderColor: "var(--fb-border)" }} />
                  <select value={m.gender} onChange={(e) => update(i, { gender: e.target.value })}
                          ref={el => { genderSelects.current[i] = el; }}
                          required aria-required="true"
                          aria-label={`Gender for ${m.name || `household member ${i + 1}`}`}
                          aria-invalid={showGenderErrors && !GENDERS.includes(m.gender)}
                          aria-describedby={showGenderErrors && !GENDERS.includes(m.gender) ? "portal-signup-gender-error" : undefined}
                          data-testid={`portal-signup-gender-${i}`}
                          className="col-span-3 md:col-span-2 min-w-0 px-2 py-2 rounded-lg border bg-white" style={{ borderColor: showGenderErrors && !GENDERS.includes(m.gender) ? "var(--fb-error)" : "var(--fb-border)" }}>
                    <option value="">Gender *</option>
                    {GENDERS.map((g) => <option key={g} value={g}>{g}</option>)}
                  </select>
                  <select value={m.relationship} onChange={(e) => update(i, { relationship: e.target.value })}
                          className="col-span-3 md:col-span-3 min-w-0 px-3 py-2 rounded-lg border bg-white" style={{ borderColor: "var(--fb-border)" }}>
                    <option value="">Relationship</option>
                    <option value="Self">Self</option>
                    {RELATIONSHIPS.map((r) => <option key={r} value={r}>{r}</option>)}
                  </select>
                  <div className="col-span-6 md:col-span-2 flex items-center justify-between gap-2">
                    <span className="text-xs font-semibold px-2 py-1 rounded text-white" style={{ background: tagBg }}>{tag}</span>
                    <button onClick={() => remove(i)} className="p-1.5 rounded hover:bg-rose-50" aria-label="Remove">
                      <Trash2 className="w-4 h-4" style={{ color: "var(--fb-error)" }} />
                    </button>
                  </div>
                </div>
                {isToy && (
                  <ToyRequestField value={m.toy_items || []} onChange={(v) => update(i, { toy_items: v })}
                    notes={m.toy_notes || ""} onNotesChange={(v) => update(i, { toy_notes: v })}
                    suggestions={toySuggestions.items} suggestionsLoading={toySuggestions.loading}
                    suggestionsError={toySuggestions.error} onRetrySuggestions={toySuggestions.retry}
                    audience="client" testIdPrefix={`portal-toy-${i}`} />
                )}
                {isToy && <SharingTreeClothingSizes member={m} onChange={(patch) => update(i, patch)} />}
              </div>
            );
          })}
        </div>

        <button onClick={add} className="text-xs font-semibold inline-flex items-center gap-1 mb-4" style={{ color: "var(--fb-primary)" }}>
          <Plus className="w-3 h-3" /> Add another member
        </button>

        <SharingTreeAdditionalInformation value={additionalInformation}
          onChange={setAdditionalInformation} testIdPrefix="portal" />

        {period?.show_family_item && (
          <div className="mb-4">
            <label className="block text-sm font-medium mb-1.5">Family item <span className="font-normal" style={{ color: "var(--fb-text-muted)" }}>(optional)</span></label>
            <input
              placeholder="e.g. Family game, Deck of cards"
              value={familyItem}
              onChange={(e) => setFamilyItem(e.target.value)}
              className="w-full px-3 py-2.5 rounded-lg border"
              style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
              data-testid="portal-family-item"
            />
          </div>
        )}

        {error && <div id="portal-signup-gender-error" role="alert" className="fb-status-error text-sm p-3 rounded-lg mb-3" data-testid="portal-signup-error">{error}</div>}

        <div className="flex justify-end gap-3">
          <button onClick={onClose} className="px-5 py-2.5 rounded-lg border" style={{ borderColor: "var(--fb-border)" }}>Cancel</button>
          <button onClick={submit} disabled={saving || membersLoading || membersLoadError}
                  className="fb-btn-primary px-5 py-2.5 rounded-lg font-semibold"
                  data-testid="portal-signup-submit">
            {saving ? "Submitting…" : "Submit Signup"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PortalField({ label, value, onChange, onBlur, testid }) {
  return (
    <div>
      <label className="block text-sm font-medium mb-1.5">{label}</label>
      <input
        data-testid={testid}
        value={value || ""} onChange={(e) => onChange(e.target.value)}
        onBlur={onBlur}
        className="w-full px-4 py-3 rounded-lg border-2 outline-none focus:border-[#052F66] text-base"
        style={{ borderColor: "var(--fb-border)", background: "#FAFAF8" }}
      />
    </div>
  );
}
